import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import FieldNoteRichEditor from "./FieldNoteRichEditor";

const API_TOKEN = process.env.REACT_APP_API_TOKEN;
const API = process.env.REACT_APP_API_FOLDER;

const REFERENCE_TYPES = [
  ["artist", "Artist"],
  ["recording", "Recording"],
  ["book", "Book"],
  ["article", "Article"],
  ["software", "Software"],
  ["website", "Website"],
  ["film-video", "Film / video"],
  ["other", "Other"],
];

function formatBytes(bytes) {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function artifactIsImage(data = {}) {
  if (typeof data.is_image === "boolean") return data.is_image;
  return /\.(jpe?g|png|webp)$/i.test(String(data.filename || ""));
}


function normaliseList(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
}

function normaliseWriterResponse(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    throw new Error(
      "The writer API returned an invalid response. Check the PHP error log / fieldnote_writer_get response."
    );
  }

  return {
    ...raw,
    media: normaliseList(raw.media).filter((v) => typeof v === "string"),
    media_filenames: normaliseList(raw.media_filenames).filter((v) => typeof v === "string"),
    artform_options: normaliseList(raw.artform_options).filter((v) => typeof v === "string"),
    blocks: normaliseList(raw.blocks).map((block) => ({
      ...(block && typeof block === "object" ? block : {}),
      data:
        block && block.data && typeof block.data === "object" && !Array.isArray(block.data)
          ? block.data
          : {},
    })),
    references: normaliseList(raw.references).map((reference) =>
      reference && typeof reference === "object" ? reference : {}
    ),
  };
}

function providerFromUrl(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    if (host.includes("youtube") || host === "youtu.be") return "youtube";
    if (host.includes("vimeo")) return "vimeo";
    if (host.includes("soundcloud") || host === "snd.sc") return "soundcloud";
    if (host.includes("mixcloud")) return "mixcloud";
    if (host.includes("bandcamp")) return "bandcamp";
  } catch (_) {}
  return "media";
}

function iframePixelDimension(node, name) {
  if (!node) return 0;
  const attr = String(node.getAttribute(name) || "").trim();
  if (/^\d+(?:\.\d+)?(?:px)?$/i.test(attr)) return Math.max(0, Math.round(parseFloat(attr)));
  const style = String(node.getAttribute("style") || "");
  const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*(\\d+(?:\\.\\d+)?)px`, "i"));
  return match ? Math.max(0, Math.round(parseFloat(match[1]))) : 0;
}

function extractIframeDetails(input) {
  const value = String(input || "").trim();
  if (!/<iframe\b/i.test(value)) return { src: value, width: 0, height: 0 };
  try {
    const doc = new DOMParser().parseFromString(value, "text/html");
    const iframe = doc.querySelector("iframe");
    return {
      src: String(iframe?.getAttribute("src") || "").trim(),
      width: iframePixelDimension(iframe, "width"),
      height: iframePixelDimension(iframe, "height"),
    };
  } catch (_) {
    const srcMatch = value.match(/<iframe\b[^>]*\bsrc=["']([^"']+)["']/i);
    const widthMatch = value.match(/(?:\bwidth=["']?(\d+)(?:px)?["']?|\bwidth\s*:\s*(\d+(?:\.\d+)?)px)/i);
    const heightMatch = value.match(/(?:\bheight=["']?(\d+)(?:px)?["']?|\bheight\s*:\s*(\d+(?:\.\d+)?)px)/i);
    return {
      src: srcMatch ? srcMatch[1].trim() : "",
      width: widthMatch ? Math.round(parseFloat(widthMatch[1] || widthMatch[2] || 0)) : 0,
      height: heightMatch ? Math.round(parseFloat(heightMatch[1] || heightMatch[2] || 0)) : 0,
    };
  }
}

function extractIframeSrc(input) {
  return extractIframeDetails(input).src;
}

function isSoundCloudPlayerUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" && parsed.hostname.replace(/^www\./, "").toLowerCase() === "w.soundcloud.com" && parsed.pathname.startsWith("/player/");
  } catch (_) { return false; }
}

function isSoundCloudShortUrl(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    return host === "on.soundcloud.com" || host === "snd.sc";
  } catch (_) { return false; }
}

function normaliseBandcampPlayerUrl(url) {
  const value = String(url || "").trim();
  if (!/https:\/\/bandcamp\.com\/EmbeddedPlayer\//i.test(value)) return "";
  return `${value.replace(/\/+$/, "")}/`;
}

function mediaUrlClientValidation(rawInput) {
  const raw = String(rawInput || "").trim();
  if (!raw) return { provider: "", valid: false, message: "" };
  const url = extractIframeSrc(raw);
  if (!url) return { provider: "media", valid: false, message: "The embed code does not contain a usable iframe src URL." };
  const provider = providerFromUrl(url);
  if (provider === "media") return { provider, valid: false, message: "Unsupported or unrecognised URL." };

  if (provider === "bandcamp") {
    if (/\/EmbeddedPlayer\//i.test(url)) {
      return { provider, valid: true, message: "Detected service: Bandcamp — player URL ready." };
    }
    try {
      const parsed = new URL(url);
      if (/\/(album|track)\/[^/?#]+/i.test(parsed.pathname)) {
        return { provider, valid: true, message: "Detected service: Bandcamp — CAMP will prepare the player from this page URL when the note saves." };
      }
    } catch (_) {}
    return { provider, valid: false, message: "Paste a Bandcamp album/track URL or the Share / Embed player URL/code." };
  }

  if (provider === "soundcloud") {
    if (isSoundCloudPlayerUrl(url)) return { provider, valid: true, message: "Detected service: SoundCloud — player URL ready." };
    if (isSoundCloudShortUrl(url)) return { provider, valid: true, message: "Detected service: SoundCloud Copy Link — CAMP will convert it automatically when the note saves." };
    return { provider, valid: true, message: "Detected service: SoundCloud — CAMP will ask SoundCloud for the player when you add it." };
  }

  return { provider, valid: true, message: `Detected service: ${provider}` };
}

function makeBlockKey() {
  if (window.crypto?.getRandomValues) {
    const bytes = new Uint8Array(12); window.crypto.getRandomValues(bytes);
    return `b_${Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("")}`;
  }
  return `b_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function statusCopy(status) {
  const map = {
    author_draft: "Working draft",
    author_submitted: "Submitted to CAMP — you can still edit until editorial review starts",
    revision_requested: "CAMP has requested revisions — editing is open",
    editorial_review: "CAMP is reviewing this Field Note — editing is locked",
    scheduled: "Scheduled for publication",
    published: "Published",
  };
  return map[status] || String(status || "").replace(/_/g, " ");
}

function blockDisplayInfo(block, allBlocks) {
  let artifactNumber = 0;
  let mediaNumber = 0;

  for (const candidate of allBlocks) {
    if (candidate?.block_type === "artifact") artifactNumber += 1;
    else mediaNumber += 1;

    if (candidate?.block_key !== block?.block_key) continue;

    const data = candidate.data || {};
    if (candidate.block_type === "artifact") {
      return {
        label: `Artifact ${artifactNumber}`,
        title: String(data.caption || data.original_name || "Attached file").trim(),
      };
    }

    const provider = String(data.provider || "media");
    const providerName = {
      youtube: "YouTube",
      vimeo: "Vimeo",
      soundcloud: "SoundCloud",
      mixcloud: "Mixcloud",
      bandcamp: "Bandcamp",
    }[provider.toLowerCase()] || "Media";

    return {
      label: `Media ${mediaNumber} · ${providerName}`,
      title: String(data.caption || providerName).trim(),
    };
  }

  return { label: "Content block", title: "" };
}


function FieldNoteProductionGuide() {
  return (
    <details className="fieldnote-guide-panel">
      <summary>Writing &amp; media guide</summary>
      <div className="fieldnote-guide-panel-body">
        <p>
          The writing should remain the main part of the Field Note. These tools are there to support the text where they genuinely help the reader.
        </p>
        <ul>
          <li><strong>Length:</strong> aim for around 700–1,200 words, and generally no more than 1,500.</li>
          <li><strong>Biography:</strong> approximately 50–100 words.</li>
          <li><strong>Gallery images:</strong> use these for general accompanying images. The first image becomes the catalogue image. You can reorder the gallery.</li>
          <li><strong>Artifacts:</strong> use these for material that belongs at a particular point in the text — sketches, scores, notebook pages, screenshots, diagrams, patches, PDFs or project files. Any file type is accepted up to 5 MB. Images display inline; other files appear as downloads.</li>
          <li><strong>Media players:</strong> audio and video are linked or embedded rather than uploaded. YouTube, Vimeo, SoundCloud, Mixcloud and Bandcamp are supported. SoundCloud Copy Link URLs are fine. For Bandcamp you can use a normal album/track URL or Share / Embed information.</li>
          <li><strong>References:</strong> add books, recordings, artists, articles, software, websites, films/video or other material mentioned in the piece. CAMP formats these automatically at the end.</li>
          <li><strong>Placement:</strong> artifacts and media appear as labelled blocks inside the article editor. Drag them by the handle to move them within the text.</li>
          <li><strong>Preview:</strong> use Preview Field Note to see the article as a reader will see it.</li>
          <li><strong>Submitting:</strong> Submit to CAMP means the piece is ready for us to review. You can still edit after submitting. Editing only locks when CAMP explicitly starts editorial review.</li>
        </ul>
        <p>
          Please only use material that you created, own, have permission to publish, or can legitimately link to or embed.
        </p>
      </div>
    </details>
  );
}

function FieldNoteWriter() {
  const { token } = useParams();
  const bodyEditorRef = useRef(null);
  const changeVersionRef = useRef(0);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [references, setReferences] = useState([]);
  const [media, setMedia] = useState([]);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [mediaUrl, setMediaUrl] = useState("");
  const [mediaCaption, setMediaCaption] = useState("");
  const [resolvingMedia, setResolvingMedia] = useState(false);
  const [artifact, setArtifact] = useState({ file: null, caption: "", credit: "", alt: "" });
  const [insertedBlockKeys, setInsertedBlockKeys] = useState([]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const result = await axios.post(`${API}fieldnote_writer_get/`, { token }, { headers: { "X-API-TOKEN": API_TOKEN } });
      const rawResponse = result.data;
      if (rawResponse && typeof rawResponse === "object" && rawResponse.error) {
        throw new Error(rawResponse.error);
      }
      const response = normaliseWriterResponse(rawResponse);
      const loadedBlocks = response.blocks;
      const hasLegacySoundCloudShortLink = loadedBlocks.some((block) => {
        const blockData = block?.data || {};
        return (
          block?.block_type === "media" &&
          String(blockData.provider || "").toLowerCase() === "soundcloud" &&
          isSoundCloudShortUrl(blockData.url)
        );
      });
      const hasLegacyBandcampPageUrl = loadedBlocks.some((block) => {
        const blockData = block?.data || {};
        if (block?.block_type !== "media" || String(blockData.provider || "").toLowerCase() !== "bandcamp") return false;
        return !normaliseBandcampPlayerUrl(blockData.embed_url || blockData.url);
      });

      setData(response);
      setDraft({ name: response.name || "", email: response.email || "", title: response.title || "", body: response.body || "<p><br></p>", biog: response.biog || "<p><br></p>", link: response.link || "", artforms: new Set(String(response.artforms || "").split(",").map((s) => s.trim()).filter(Boolean)) });
      setBlocks(loadedBlocks); setReferences(response.references); setMedia(response.media); setMediaFiles(response.media_filenames);

      // Legacy on.soundcloud.com records are deliberately marked dirty. The
      // next autosave sends them to PHP, which follows the short-link redirect
      // and stores the canonical soundcloud.com URL returned by SoundCloud.
      // This removes the fragile browser-side JSONP conversion used previously.
      const hasLegacyResolvableMedia = hasLegacySoundCloudShortLink || hasLegacyBandcampPageUrl;
      changeVersionRef.current = hasLegacyResolvableMedia ? 1 : 0;
      setDirty(hasLegacyResolvableMedia);
      if (hasLegacySoundCloudShortLink && hasLegacyBandcampPageUrl) {
        setNotice("Older SoundCloud/Bandcamp links were found. CAMP will prepare their player URLs automatically on the next autosave.");
      } else if (hasLegacySoundCloudShortLink) {
        setNotice("An older SoundCloud Copy Link was found. CAMP will convert it automatically on the next autosave.");
      } else if (hasLegacyBandcampPageUrl) {
        setNotice("An older Bandcamp page URL was found. CAMP will prepare its embedded player automatically on the next autosave.");
      }
    } catch (err) { setError(err?.response?.data?.error || err.message || "This writer link could not be loaded."); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const meta = document.createElement("meta"); meta.name = "referrer"; meta.content = "no-referrer"; meta.dataset.fieldnotesWriter = "1"; document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const canEdit = !!data?.can_edit;
  const markDirty = () => { changeVersionRef.current += 1; setDirty(true); };
  const changeDraft = (key, value) => { setDraft((d) => ({ ...d, [key]: value })); markDirty(); };
  const toggleArtform = (art) => { setDraft((d) => { const next = new Set(d.artforms); next.has(art) ? next.delete(art) : next.add(art); return { ...d, artforms: next }; }); markDirty(); };
  const changeBlocks = (next) => { setBlocks(next); markDirty(); };
  const changeRefs = (next) => { setReferences(next); markDirty(); };

  const payload = useMemo(() => draft ? ({
    token,
    name: draft.name, email: draft.email, title: draft.title, body: draft.body, biog: draft.biog, link: draft.link,
    artforms: [...draft.artforms], media_filenames: mediaFiles,
    blocks: blocks.map((b, i) => ({ ...b, sort_order: i })),
    references: references.map((r, i) => ({ ...r, sort_order: i })),
  }) : null, [draft, blocks, references, mediaFiles, token]);

  const save = useCallback(async (submit = false, quiet = false) => {
    if (!payload || !canEdit || saving) return false;
    const savingVersion = changeVersionRef.current;
    setSaving(true); if (!quiet) setNotice(""); setError("");
    try {
      const { data: response } = await axios.post(`${API}fieldnote_writer_action/`, { ...payload, action: submit ? "submit" : "save" }, { headers: { "X-API-TOKEN": API_TOKEN, "Content-Type": "application/json" } });
      if (response?.error) throw new Error(response.error);
      if (changeVersionRef.current === savingVersion) {
        if (Array.isArray(response?.blocks)) setBlocks(response.blocks);
        setDirty(false);
      }
      setData((d) => ({ ...d, editorial_status: response.editorial_status || d.editorial_status, can_edit: true, revision_message: submit ? null : d.revision_message }));
      const mediaWarnings = Array.isArray(response?.warnings) ? response.warnings.filter(Boolean) : [];
      if (mediaWarnings.length) setError(mediaWarnings.join(" "));
      if (!quiet) setNotice(response.message || "Saved."); return true;
    } catch (err) {
      const msg = err?.response?.data?.errors?.join(" ") || err?.response?.data?.error || err.message || "Save failed.";
      setError(msg); if (err?.response?.data?.locked) setData((d) => ({ ...d, can_edit: false, editorial_status: "editorial_review" })); return false;
    } finally { setSaving(false); }
  }, [payload, canEdit, saving]);

  useEffect(() => {
    if (!dirty || !canEdit || saving || !payload) return undefined;
    const timer = window.setTimeout(() => save(false, true), 5000);
    return () => window.clearTimeout(timer);
  }, [dirty, canEdit, saving, payload, save]);

  const upload = async (kind, file, extra = {}, onProgress = null) => {
    const form = new FormData(); form.append("action", kind); form.append("token", token); form.append("file", file);
    Object.entries(extra).forEach(([k, v]) => form.append(k, v || ""));
    const { data: response } = await axios.post(`${API}fieldnote_writer_action/`, form, {
      headers: { "X-API-TOKEN": API_TOKEN },
      onUploadProgress: (progressEvent) => {
        if (!onProgress) return;
        const total = Number(progressEvent.total || file.size || 0);
        const loaded = Math.min(Number(progressEvent.loaded || 0), total || Number(progressEvent.loaded || 0));
        const percent = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;
        onProgress({ loaded, total, percent });
      },
    });
    if (response?.error) throw new Error(response.error); return response;
  };

  const uploadGallery = async (event) => {
    const input = event.target;
    const files = Array.from(input.files || []); if (!files.length) return;
    setSaving(true); setError(""); setNotice("");
    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        setUploadProgress({
          kind: "gallery",
          filename: file.name,
          fileIndex: index + 1,
          fileCount: files.length,
          percent: 0,
          overallPercent: Math.round((index / files.length) * 100),
          phase: "Uploading",
        });

        const r = await upload("upload_gallery", file, {}, ({ percent }) => {
          const thisPercent = percent === null ? 0 : percent;
          const overallPercent = Math.min(100, Math.round(((index + (thisPercent / 100)) / files.length) * 100));
          setUploadProgress({
            kind: "gallery",
            filename: file.name,
            fileIndex: index + 1,
            fileCount: files.length,
            percent: thisPercent,
            overallPercent,
            phase: thisPercent >= 100 ? "Processing file" : "Uploading",
          });
        });

        setMedia((m) => [...m, r.url]);
        setMediaFiles((m) => [...m, r.filename]);
      }
      setNotice(files.length === 1 ? "Image uploaded." : `${files.length} images uploaded.`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Upload failed.");
    } finally {
      setUploadProgress(null);
      setSaving(false);
      input.value = "";
    }
  };

  const uploadArtifact = async () => {
    if (!artifact.file) return;
    const file = artifact.file;
    setSaving(true); setError(""); setNotice("");
    setUploadProgress({ kind: "artifact", filename: file.name, percent: 0, overallPercent: 0, phase: "Uploading" });
    try {
      const r = await upload(
        "upload_artifact",
        file,
        { caption: artifact.caption, credit: artifact.credit, alt: artifact.alt },
        ({ percent }) => {
          const thisPercent = percent === null ? 0 : percent;
          setUploadProgress({
            kind: "artifact",
            filename: file.name,
            percent: thisPercent,
            overallPercent: thisPercent,
            phase: thisPercent >= 100 ? "Processing file" : "Uploading",
          });
        }
      );
      const next = [...blocks, r.block]; setBlocks(next); bodyEditorRef.current?.insertBlock(r.block); setArtifact({ file: null, caption: "", credit: "", alt: "" }); markDirty(); setNotice("Artifact added and inserted into the article.");
    } catch (err) { setError(err?.response?.data?.error || err.message || "Artifact upload failed."); }
    finally { setUploadProgress(null); setSaving(false); }
  };

  const uploadProgressDisplay = uploadProgress ? (() => {
    const shownPercent = Number.isFinite(uploadProgress.overallPercent) ? uploadProgress.overallPercent : uploadProgress.percent;
    const suffix = uploadProgress.fileCount > 1
      ? ` (${uploadProgress.fileIndex} of ${uploadProgress.fileCount})`
      : "";
    const processing = /processing/i.test(uploadProgress.phase || "");
    return (
      <div className="fieldnote-upload-status" role="status" aria-live="polite">
        <div className="fieldnote-upload-status-head">
          <strong>{uploadProgress.phase}{suffix}</strong>
          {!processing && Number.isFinite(shownPercent) && <span>{shownPercent}%</span>}
        </div>
        <div className="fieldnote-upload-filename">{uploadProgress.filename}</div>
        <div className={`fieldnote-upload-track ${processing ? "is-processing" : ""}`} aria-label="File upload progress">
          <div
            className="fieldnote-upload-bar"
            style={{ width: processing ? "100%" : `${Number.isFinite(shownPercent) ? shownPercent : 0}%` }}
          />
        </div>
        <small>{processing ? "Upload complete. CAMP is preparing the file…" : "Uploading automatically — you can keep this page open."}</small>
      </div>
    );
  })() : null;

  const addMediaBlock = async () => {
    const validation = mediaUrlClientValidation(mediaUrl);
    if (!validation.valid || resolvingMedia) return;

    const iframeDetails = extractIframeDetails(mediaUrl);
    const sourceUrl = iframeDetails.src;
    const provider = providerFromUrl(sourceUrl);
    setResolvingMedia(true);
    setError("");
    setNotice("");

    try {
      let blockData = { provider, url: sourceUrl, caption: mediaCaption.trim(), embed_url: "", embed_height: 0, embed_width: 0 };

      if (provider === "soundcloud") {
        if (isSoundCloudPlayerUrl(sourceUrl)) {
          blockData = { ...blockData, embed_url: sourceUrl, embed_height: iframeDetails.height > 0 ? iframeDetails.height : 166 };
        }
        // Normal soundcloud.com URLs render directly. Copy Link URLs are
        // canonicalised by PHP on save, which is more reliable than trying to
        // resolve provider redirects from the browser.
      } else if (provider === "bandcamp") {
        const playerUrl = normaliseBandcampPlayerUrl(sourceUrl);
        if (playerUrl) {
          blockData = {
            ...blockData,
            url: playerUrl,
            embed_url: playerUrl,
            embed_height: iframeDetails.height > 0 ? iframeDetails.height : (/\/size=small\//i.test(playerUrl) ? 42 : (/\/size=large\//i.test(playerUrl) ? 470 : 120)),
            embed_width: iframeDetails.width > 0 ? iframeDetails.width : (/\/size=large\//i.test(playerUrl) ? 350 : 0),
          };
        }
        // Normal artist.bandcamp.com album/track URLs are accepted too. PHP
        // resolves those once on save and returns the cached EmbeddedPlayer URL.
      }

      const block = { block_key: makeBlockKey(), block_type: "media", data: blockData, sort_order: blocks.length };
      setBlocks((b) => [...b, block]);
      bodyEditorRef.current?.insertBlock(block);
      setMediaUrl("");
      setMediaCaption("");
      markDirty();
      setNotice(provider === "soundcloud" && isSoundCloudShortUrl(sourceUrl)
        ? "SoundCloud player added. CAMP will convert the Copy Link automatically when it saves."
        : provider === "bandcamp" && !normaliseBandcampPlayerUrl(sourceUrl)
          ? "Bandcamp player added. CAMP will prepare the embedded player automatically when it saves."
          : `${provider === "soundcloud" ? "SoundCloud" : provider === "bandcamp" ? "Bandcamp" : "Media"} player added.`);
    } catch (err) {
      setError(err?.message || "The media player could not be prepared.");
    } finally {
      setResolvingMedia(false);
    }
  };

  const updateExistingMediaInput = (index, rawInput) => {
    const iframeDetails = extractIframeDetails(rawInput);
    const sourceUrl = iframeDetails.src;
    const provider = providerFromUrl(sourceUrl);
    let nextData = {
      ...(blocks[index]?.data || {}),
      provider,
      url: sourceUrl,
      embed_url: "",
      embed_height: 0,
      embed_width: 0,
    };

    if (provider === "soundcloud" && isSoundCloudPlayerUrl(sourceUrl)) {
      nextData = {
        ...nextData,
        embed_url: sourceUrl,
        embed_height: iframeDetails.height > 0 ? iframeDetails.height : 166,
      };
    } else if (provider === "bandcamp") {
      const playerUrl = normaliseBandcampPlayerUrl(sourceUrl);
      if (playerUrl) {
        nextData = {
          ...nextData,
          url: playerUrl,
          embed_url: playerUrl,
          embed_height: iframeDetails.height > 0 ? iframeDetails.height : (/\/size=small\//i.test(playerUrl) ? 42 : (/\/size=large\//i.test(playerUrl) ? 470 : 120)),
          embed_width: iframeDetails.width > 0 ? iframeDetails.width : (/\/size=large\//i.test(playerUrl) ? 350 : 0),
        };
      }
    }

    changeBlocks(blocks.map((block, blockIndex) => blockIndex === index ? { ...block, data: nextData } : block));
  };

  const removeBlock = (key) => { bodyEditorRef.current?.removeMarker(key); changeBlocks(blocks.filter((b) => b.block_key !== key)); };
  const moveMedia = (index, delta) => { const to = index + delta; if (to < 0 || to >= mediaFiles.length) return; const files = [...mediaFiles]; const urls = [...media]; [files[index], files[to]] = [files[to], files[index]]; [urls[index], urls[to]] = [urls[to], urls[index]]; setMediaFiles(files); setMedia(urls); markDirty(); };
  const removeGallery = (index) => { setMediaFiles((m) => m.filter((_, i) => i !== index)); setMedia((m) => m.filter((_, i) => i !== index)); markDirty(); };

  const jumpToBlockDetails = useCallback((blockKey) => {
    const element = document.getElementById(`fieldnote-block-card-${blockKey}`);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.classList.remove("fieldnote-writer-block--flash");
    window.requestAnimationFrame(() => {
      element.classList.add("fieldnote-writer-block--flash");
      window.setTimeout(() => element.classList.remove("fieldnote-writer-block--flash"), 1600);
    });
  }, []);

  if (loading) return <section className="fieldnote-workspace-page pt-110 section-bottom-pad"><div className="container"><p>Loading writer workspace…</p></div></section>;
  if (error && !draft) return <section className="fieldnote-workspace-page pt-110 section-bottom-pad"><div className="container"><div className="alert alert-danger">{error}</div></div></section>;
  if (!draft) return null;

  return <section className="fieldnote-workspace-page pt-110 section-bottom-pad"><div className="container"><div className="fieldnote-writer-shell">
    <div className="fieldnote-writer-top"><div><span className="fieldnote-kicker">CAMP FIELD NOTES</span><h1>Writer workspace</h1><p>{statusCopy(data.editorial_status)}</p></div><div className="fieldnote-save-state">{saving ? "Saving…" : dirty ? "Unsaved changes" : "Saved"}</div></div>
    {data.revision_message && <div className="alert alert-warning"><strong>CAMP has requested changes:</strong><br />{data.revision_message}</div>}
    {!canEdit && <div className="alert alert-primary">CAMP has started editorial review. Your workspace is now read-only.</div>}
    {error && <div className="alert alert-danger">{error}</div>}{notice && <div className="alert alert-success">{notice}</div>}

    <FieldNoteProductionGuide />

    <div className="fieldnote-writer-card"><h2>Story</h2><div className="fieldnote-form-grid"><label>Title<input className="form-control" disabled={!canEdit} value={draft.title} onChange={(e) => changeDraft("title", e.target.value)} /></label><label>Author name<input className="form-control" disabled={!canEdit} value={draft.name} onChange={(e) => changeDraft("name", e.target.value)} /></label></div><label>Email<input className="form-control" type="email" disabled={!canEdit} value={draft.email} onChange={(e) => changeDraft("email", e.target.value)} /></label><label>Website/profile<input className="form-control" disabled={!canEdit} value={draft.link} onChange={(e) => changeDraft("link", e.target.value)} /></label><label>Main text</label><p className="fieldnote-help-text">Artifacts and media appear here as labelled cards. Click a card in the text to jump to its details below.</p><FieldNoteRichEditor ref={bodyEditorRef} value={draft.body} disabled={!canEdit} blocks={blocks} onBlockClick={jumpToBlockDetails} onBlockPresenceChange={setInsertedBlockKeys} onChange={(html) => changeDraft("body", html)} /></div>

    <div className="fieldnote-writer-card">
      <h2>Images</h2>
      <p>These form the general gallery. The first image is used on the Field Notes catalogue.</p>
      <div className="fieldnote-writer-gallery">
        {media.map((url, i) => (
          <div className="fieldnote-writer-gallery-item" key={`${mediaFiles[i]}-${i}`}>
            <img src={url} alt="" />
            <div className="fieldnote-gallery-actions">
              <button type="button" disabled={!canEdit || saving || i===0} onClick={() => moveMedia(i,-1)}>←</button>
              <button type="button" disabled={!canEdit || saving || i===media.length-1} onClick={() => moveMedia(i,1)}>→</button>
              <button type="button" disabled={!canEdit || saving} onClick={() => removeGallery(i)}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      {canEdit && (
        <>
          <label className="fieldnote-upload-label mt-3">Add gallery images</label>
          <p className="fieldnote-help-text">Choose JPG, PNG or WebP files. Selecting files starts the upload immediately.</p>
          <input className="form-control" type="file" multiple accept="image/jpeg,image/png,image/webp" disabled={saving} onChange={uploadGallery} />
          {uploadProgress?.kind === "gallery" && uploadProgressDisplay}
        </>
      )}
    </div>

    <div className="fieldnote-writer-card">
      <h2>Artifacts and media inside the article</h2>
      <p>Add working material or players. They appear in the article as clear labelled cards rather than technical shortcodes.</p>

      {blocks.map((block, index) => {
        const blockData = block.data || {};
        const imageArtifact = block.block_type === "artifact" && artifactIsImage(blockData);
        const display = blockDisplayInfo(block, blocks);
        const isPlaced = insertedBlockKeys.includes(block.block_key);

        return (
          <div
            className="fieldnote-writer-block"
            id={`fieldnote-block-card-${block.block_key}`}
            key={block.block_key}
          >
            <div className="fieldnote-block-head">
              <div>
                <strong>{display.label}{display.title ? ` — ${display.title}` : ""}</strong>
                <span className={`fieldnote-block-placement ${isPlaced ? "is-placed" : ""}`}>
                  {isPlaced ? "Placed in article ✓" : "Not yet placed in article"}
                </span>
              </div>
            </div>
            {block.block_type === "artifact" ? (
              <>
                {imageArtifact && blockData.url ? (
                  <img className="fieldnote-block-thumb" src={blockData.url} alt="" />
                ) : (
                  <div className="fieldnote-artifact-file-summary">
                    <strong>{blockData.original_name || "Attached file"}</strong>
                    <span>{[blockData.mime, formatBytes(blockData.size)].filter(Boolean).join(" · ")}</span>
                  </div>
                )}
                <input className="form-control" disabled={!canEdit} placeholder="Caption" value={blockData.caption || ""} onChange={(e) => changeBlocks(blocks.map((b,j)=>j===index?{...b,data:{...b.data,caption:e.target.value}}:b))} />
                <input className="form-control" disabled={!canEdit} placeholder="Credit / source" value={blockData.credit || ""} onChange={(e) => changeBlocks(blocks.map((b,j)=>j===index?{...b,data:{...b.data,credit:e.target.value}}:b))} />
                {imageArtifact && <input className="form-control" disabled={!canEdit} placeholder="Alt text" value={blockData.alt || ""} onChange={(e) => changeBlocks(blocks.map((b,j)=>j===index?{...b,data:{...b.data,alt:e.target.value}}:b))} />}
              </>
            ) : (
              <>
                <textarea
                  className="form-control"
                  rows="2"
                  disabled={!canEdit}
                  value={blockData.url || ""}
                  onChange={(e) => updateExistingMediaInput(index, e.target.value)}
                  placeholder="Paste provider URL or supported iframe code"
                />
                {String(blockData.provider || "").toLowerCase() === "soundcloud" && isSoundCloudShortUrl(blockData.url) && (
                  <small className="fieldnote-media-detected">SoundCloud Copy Link — CAMP will convert this automatically on save.</small>
                )}
                {String(blockData.provider || "").toLowerCase() === "bandcamp" && !normaliseBandcampPlayerUrl(blockData.embed_url || blockData.url) && (
                  <small className="fieldnote-media-detected">Bandcamp page URL — CAMP will prepare and cache the embedded player on save.</small>
                )}
                <input className="form-control" disabled={!canEdit} placeholder="Caption" value={blockData.caption || ""} onChange={(e) => changeBlocks(blocks.map((b,j)=>j===index?{...b,data:{...b.data,caption:e.target.value}}:b))} />
              </>
            )}
            <div className="fieldnote-block-actions">
              {isPlaced ? (
                <button className="btn btn-sm btn-outline-primary" type="button" onClick={() => bodyEditorRef.current?.focusBlock(block.block_key)}>Show in article</button>
              ) : (
                <button className="btn btn-sm btn-outline-primary" type="button" disabled={!canEdit} onClick={() => bodyEditorRef.current?.insertBlock(block)}>Insert into article</button>
              )}
              <button className="btn btn-sm btn-outline-danger" type="button" disabled={!canEdit} onClick={() => removeBlock(block.block_key)}>Remove</button>
            </div>
          </div>
        );
      })}

      {canEdit && (
        <div className="fieldnote-add-grid">
          <div>
            <h3>Add artifact</h3>
            <p className="fieldnote-help-text">Upload any file up to 5 MB — for example an image, PDF, score, patch, text file or project file. Images display inline; other files appear as downloadable attachments.</p>
            <input className="form-control" type="file" disabled={saving} onChange={(e)=>setArtifact((a)=>({...a,file:e.target.files?.[0]||null}))}/>
            {artifact.file && <small className="fieldnote-selected-file">Selected: {artifact.file.name} ({formatBytes(artifact.file.size)})</small>}
            <input className="form-control" placeholder="Caption" value={artifact.caption} disabled={saving} onChange={(e)=>setArtifact((a)=>({...a,caption:e.target.value}))}/>
            <input className="form-control" placeholder="Credit / source" value={artifact.credit} disabled={saving} onChange={(e)=>setArtifact((a)=>({...a,credit:e.target.value}))}/>
            {artifact.file?.type?.startsWith("image/") && <input className="form-control" placeholder="Alt text" value={artifact.alt} disabled={saving} onChange={(e)=>setArtifact((a)=>({...a,alt:e.target.value}))}/>}
            <button className="btn btn-outline-primary" type="button" onClick={uploadArtifact} disabled={!artifact.file || saving}>Upload and insert artifact</button>
            {uploadProgress?.kind === "artifact" && uploadProgressDisplay}
          </div>

          <div>
            <h3>Add media player</h3>
            <p className="fieldnote-help-text"><strong>Paste a normal public/share link.</strong> YouTube, Vimeo and Mixcloud accept their normal URLs. For <strong>SoundCloud</strong>, either the full soundcloud.com URL or the short URL from Copy Link works. For <strong>Bandcamp</strong>, either a normal album/track URL or the Share / Embed player URL/code works. CAMP converts supported links into the correct player automatically.</p>
            <label className="fieldnote-upload-label">Media URL</label>
            <textarea className="form-control" rows="2" placeholder="Paste a media URL, or Bandcamp/SoundCloud iframe code…" value={mediaUrl} onChange={(e)=>setMediaUrl(e.target.value)}/>
            {mediaUrl.trim() && <small className="fieldnote-media-detected">{mediaUrlClientValidation(mediaUrl).message}</small>}
            <input className="form-control" placeholder="Caption (optional)" value={mediaCaption} onChange={(e)=>setMediaCaption(e.target.value)}/>
            <button className="btn btn-outline-primary" type="button" onClick={addMediaBlock} disabled={!mediaUrlClientValidation(mediaUrl).valid || resolvingMedia}>{resolvingMedia ? "Preparing player…" : "Add media player"}</button>
          </div>
        </div>
      )}
    </div>

    <div className="fieldnote-writer-card">
      <h2>References / things mentioned</h2>
      <p className="fieldnote-help-text">Add books, recordings, artists, software, articles or other material mentioned in the Field Note. These are formatted automatically at the end of the article.</p>
      {references.map((ref,i)=>(
        <div className="fieldnote-reference-edit" key={ref.id || i}>
          <div className="fieldnote-form-grid">
            <select className="form-select" disabled={!canEdit} value={ref.reference_type || "other"} onChange={(e)=>changeRefs(references.map((r,j)=>j===i?{...r,reference_type:e.target.value}:r))}>
              {REFERENCE_TYPES.map(([value,label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <input className="form-control" disabled={!canEdit} placeholder="Title" value={ref.title || ""} onChange={(e)=>changeRefs(references.map((r,j)=>j===i?{...r,title:e.target.value}:r))}/>
            <input className="form-control" disabled={!canEdit} placeholder="Creator / author" value={ref.creator || ""} onChange={(e)=>changeRefs(references.map((r,j)=>j===i?{...r,creator:e.target.value}:r))}/>
            <input className="form-control" disabled={!canEdit} placeholder="URL (optional)" value={ref.url || ""} onChange={(e)=>changeRefs(references.map((r,j)=>j===i?{...r,url:e.target.value}:r))}/>
          </div>
          <textarea className="form-control" disabled={!canEdit} rows="2" placeholder="Optional note" value={ref.note || ""} onChange={(e)=>changeRefs(references.map((r,j)=>j===i?{...r,note:e.target.value}:r))}/>
          {canEdit&&<button className="btn btn-sm btn-outline-danger" type="button" onClick={()=>changeRefs(references.filter((_,j)=>j!==i))}>Remove</button>}
        </div>
      ))}
      {canEdit&&<button className="btn btn-outline-primary" type="button" onClick={()=>changeRefs([...references,{reference_type:"other",title:"",creator:"",url:"",note:""}])}>+ Add reference</button>}
    </div>

    <div className="fieldnote-writer-card"><h2>Biography</h2><FieldNoteRichEditor value={draft.biog} small disabled={!canEdit} onChange={(html) => changeDraft("biog", html)} /><h3 className="mt-4">Artforms / subjects</h3><div className="fieldnote-artform-grid">{(data.artform_options || []).map((art)=><label className="fieldnote-artform-choice" key={art}><input type="checkbox" disabled={!canEdit} checked={draft.artforms.has(art)} onChange={()=>toggleArtform(art)}/> {art}</label>)}</div></div>

    <div className="fieldnote-writer-actions"><a className="btn btn-outline-secondary" target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" href={`/fieldnotes/preview/${token}`}>Preview Field Note</a>{canEdit&&<><button className="btn btn-outline-primary" type="button" disabled={saving} onClick={()=>save(false)}>{saving?"Saving…":"Save draft"}</button><button className="btn btn-primary" type="button" disabled={saving} onClick={()=>save(true)}>Submit to CAMP</button></>}</div>
    {data.editorial_status === "author_submitted" && <p className="fieldnote-submit-help">You can continue editing after submission. Editing only locks when CAMP explicitly starts editorial review.</p>}
  </div></div></section>;
}
export default FieldNoteWriter;
